import {Injectable} from "@angular/core";
import {Share} from '@capacitor/share';
import {CreateProductDeepLinkUseCase} from "@app/core/shared/application/create-product-deep-link-use-case.service";
import {take, tap} from "rxjs/operators";
import {ProductDetails} from "@app/core/product/domain/dtos/product-details";
import {Observable} from "rxjs";
import {ClipboardService} from "@app/presentation/services/clipboard/clipboard.service";
import {TranslateService} from "@ngx-translate/core";
import {environment} from "src/environments/environment";

interface ShareOptions {
    //Set a title for any message. This will be the subject if sharing to email
    title?: string,

    //Set a URL to share, can be http, https or file:// URL
    url?: string,

    //Set a title for the share modal. This option is only supported on Android
    dialogTitle?: string,

    //Set a text to share
    text?: string,
}

@Injectable()
export class ShareService {

    constructor(
        private createProductDeepLinkUseCase: CreateProductDeepLinkUseCase,
        private clipboardService: ClipboardService,
        private translateService: TranslateService,
    ) {
    }

    /**
     * It takes a product, creates a deep link for it, and then shares the deep link
     * @param {ProductDetails} product - ProductDetails - The product to share
     * @returns Observable<string>
     */
    shareProduct(product: ProductDetails): Observable<string> {
        return this.createProductDeepLinkUseCase.execute(
            product,
            this.translateService.instant('share.share_product_link_title')
        ).pipe(
                take(1),
                tap(productDeepLink => {
                    this.share({
                        dialogTitle: this.translateService.instant('share.share_product_title'),
                        url: productDeepLink
                    })
                }),
            );
    }

    shareApp() {
        this.share({
            url: environment.OPEN_APP_LINK,
        })
    }


    /**
     * Displays the native share dialog with the specified options.
     * If the sharing functionality is unavailable and a URL is provided,
     * the URL is copied to the clipboard as a fallback.
     */
    share(options: ShareOptions) {
            Share.share({
                title: options.title,
                url: options.url,
                dialogTitle: options.dialogTitle,
                text: options.text,
            }).catch(error => {
                if(error.code == 'UNAVAILABLE' && (options.url != null)) {
                    this.clipboardService.copyToClipboard(options.url);
                }
            });

    }
}
